import { call, put, delay, take, fork, cancel, cancelled } from '@redux-saga/core/effects'
import { createRoutine } from 'redux-saga-routines'
import * as guruChatService from 'services/GuruChatService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import { requestAiStatusRefresh } from 'utils/aiStatus'

export const loadHistoryRoutine = createRoutine('GURU_CHAT_LOAD_HISTORY')
export const sendMessageRoutine = createRoutine('GURU_CHAT_SEND_MESSAGE')
export const pollMessageRoutine = createRoutine('GURU_CHAT_POLL_MESSAGE')
export const confirmActionRoutine = createRoutine('GURU_CHAT_CONFIRM_ACTION')
export const clearChatRoutine = createRoutine('GURU_CHAT_CLEAR')

const POLL_INTERVAL = 1000
const IDLE_TIMEOUT = 60000
const HARD_TIMEOUT = 300000

function* pollForResult(bpId, messageId, portfolioId, isUserMode, estateId) {
  const startTime = Date.now()
  let lastActivity = Date.now()
  let lastSignature = ''
  const statusFn = isUserMode
    ? guruChatService.getUserChatStatus
    : (portfolioId ? guruChatService.getPortfolioChatStatus : (estateId ? guruChatService.getEstateChatStatus : guruChatService.getChatStatus))
  const statusArgs = isUserMode
    ? { messageId }
    : (portfolioId ? { portfolioId, messageId } : (estateId ? { estateId, messageId } : { bpId, messageId }))

  const timedOut = function* () {
    yield put(pollMessageRoutine.failure({
      status: 'error',
      errorType: 'timeout',
      message: 'Request timed out. Please try again.',
    }))
  }

  try {
    while (Date.now() - startTime < HARD_TIMEOUT) {
      const { data } = yield call(statusFn, statusArgs)

      if (data.status === 'complete') {
        yield put(pollMessageRoutine.success(data))
        return
      }

      if (data.status === 'error') {
        yield call(requestAiStatusRefresh)
        yield put(pollMessageRoutine.failure(data))
        return
      }

      const signature = `${data.toolStatus || ''}|${(data.partial || '').length}`
      if (signature !== lastSignature) {
        lastActivity = Date.now()
        lastSignature = signature
      }

      yield put(pollMessageRoutine.request({ toolStatus: data.toolStatus, partial: data.partial }))

      if (Date.now() - lastActivity > IDLE_TIMEOUT) {
        yield* timedOut()
        return
      }

      yield delay(POLL_INTERVAL)
    }

    yield* timedOut()
  } finally {
    if (yield cancelled()) {
      yield put(pollMessageRoutine.fulfill())
    }
  }
}

function* sendMessage({ payload }) {
  const { bpId, question, portfolioId, userMode, estateId } = payload
  yield put(sendMessageRoutine.request())

  try {
    let startFn
    let startArgs
    if (userMode) {
      startFn = guruChatService.startUserChat
      startArgs = { question }
    } else if (portfolioId) {
      startFn = guruChatService.startPortfolioChat
      startArgs = { portfolioId, question }
    } else if (estateId) {
      startFn = guruChatService.startEstateChat
      startArgs = { estateId, question }
    } else {
      startFn = guruChatService.startChat
      startArgs = { bpId, question }
    }
    const { data } = yield call(startFn, startArgs)
    yield put(sendMessageRoutine.success({ messageId: data.messageId, question }))

    // Start polling
    yield call(pollForResult, bpId, data.messageId, portfolioId, userMode, estateId)
  } catch (error) {
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 60
      yield put(sendMessageRoutine.failure({
        errorType: 'rate_limit',
        message: `You've reached the question limit. Try again in ${Math.ceil(retryAfter / 60)} minutes.`,
        retryAfter: Number(retryAfter),
      }))
    } else {
      yield put(sendMessageRoutine.failure(getApiErrors(error)))
      toast.error('Failed to send message')
    }
  } finally {
    yield put(sendMessageRoutine.fulfill())
  }
}

function* confirmAction({ payload }) {
  const { bpId, token, edits } = payload
  yield put(confirmActionRoutine.request())

  try {
    const { data } = yield call(guruChatService.confirmAction, { bpId, token, edits })
    yield put(confirmActionRoutine.success(data))
    toast.success('Action completed!')
  } catch (error) {
    yield put(confirmActionRoutine.failure(getApiErrors(error)))
    toast.error('Failed to confirm action')
  } finally {
    yield put(confirmActionRoutine.fulfill())
  }
}

function* loadHistory({ payload }) {
  const { bpId, portfolioId, userMode, estateId } = payload
  try {
    let historyFn
    let historyArgs
    if (userMode) {
      historyFn = guruChatService.getUserChatHistory
      historyArgs = undefined
    } else if (portfolioId) {
      historyFn = guruChatService.getPortfolioChatHistory
      historyArgs = { portfolioId }
    } else if (estateId) {
      historyFn = guruChatService.getEstateChatHistory
      historyArgs = { estateId }
    } else {
      historyFn = guruChatService.getChatHistory
      historyArgs = { bpId }
    }
    const { data } = yield call(historyFn, historyArgs)
    yield put(loadHistoryRoutine.success(data.messages || []))
  } catch (error) {
    // Silently fail — just means no history to show
    yield put(loadHistoryRoutine.failure())
  }
}

// Watchers
function* loadHistoryWatcher() {
  while (true) {
    const action = yield take(loadHistoryRoutine.TRIGGER)
    yield fork(loadHistory, action)
  }
}

function* sendMessageWatcher() {
  let pollTask = null
  while (true) {
    const action = yield take(sendMessageRoutine.TRIGGER)
    if (pollTask) {
      yield cancel(pollTask)
    }
    pollTask = yield fork(sendMessage, action)
  }
}

function* confirmActionWatcher() {
  while (true) {
    const action = yield take(confirmActionRoutine.TRIGGER)
    yield fork(confirmAction, action)
  }
}

export function* guruChatSaga() {
  yield fork(loadHistoryWatcher)
  yield fork(sendMessageWatcher)
  yield fork(confirmActionWatcher)
}
